import { getBaseUrl } from "@/lib/site";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation, useNavigate, Navigate } from "react-router-dom";
import { lazy, Suspense, useEffect } from "react";
import { Capacitor } from "@capacitor/core";
import { App as CapacitorApp } from "@capacitor/app";
import { AuthProvider } from "@/contexts/AuthContext";
import { useAuth } from "@/contexts/AuthContext";
import { BusinessProvider } from "@/hooks/use-business";
import { useBusiness } from "@/hooks/use-business";
import { LocationProvider } from "@/contexts/LocationContext";
import { SubscriptionGate } from "@/components/subscription/SubscriptionGate";
import { TrialGate } from "@/components/trial/TrialGate";
import { clearNativePushExternalUser, isNativePushPlatform, syncNativePushSubscription } from "@/lib/native-push";
import { isNativeApp } from "@/lib/platform";
import Index from "./pages/Index";
import SignIn from "./pages/SignIn";
import SignUp from "./pages/SignUp";
import SignUpVerify from "./pages/SignUpVerify";
import Dashboard from "./pages/app/Dashboard";
import Appointments from "./pages/app/Appointments";
import Services from "./pages/app/Services";
import Customers from "./pages/app/Customers";
import Marketing from "./pages/app/Marketing";
import Analytics from "./pages/app/Analytics";
import Settings from "./pages/app/Settings";
import Billing from "./pages/app/Billing";
import BookingPagePreview from "./pages/app/BookingPagePreview";
import Memberships from "./pages/app/Memberships";
import AccessDesk from "./pages/app/AccessDesk";
import AccessDeskTicket from "./pages/app/AccessDeskTicket";
import Events from "./pages/app/Events";
import EventPreview from "./pages/app/EventPreview";
import EventPage from "./pages/public/EventPage";
import EventTicket from "./pages/public/EventTicket";
import Invoicing from "./pages/app/Invoicing";
import InvoicePreview from "./pages/app/InvoicePreview";
import InvoicePage from "./pages/public/InvoicePage";
import MembershipRenewalPage from "./pages/public/MembershipRenewalPage";
import Orders from "./pages/app/Orders";
import MenuItems from "./pages/app/MenuItems";
import OrdersPagePreview from "./pages/app/OrdersPagePreview";
import MenuItemsManager from "./pages/app/MenuItemsManager";
import FoodOrders from "./pages/app/FoodOrders";
import CateringRequestsPage from "./pages/app/CateringRequestsPage";
import OrdersPublicPage from "./pages/public/OrdersPublicPage";
import CateringRequestPage from "./pages/public/CateringRequestPage";
import ServiceReviewPage from "./pages/public/ServiceReviewPage";
import ServiceFeedbackPage from "./pages/public/ServiceFeedbackPage";
import FoodOrderPage from "./pages/public/FoodOrderPage";
import BookingPage from "./pages/book/BookingPage";
import ManageBooking from "./pages/book/ManageBooking";
import Pricing from "./pages/Pricing";
import Solutions from "./pages/Solutions";
import FAQ from "./pages/FAQ";
import Terms from "./pages/Terms";
import Privacy from "./pages/Privacy";
import DeleteAccount from "./pages/DeleteAccount";
import Staff from "./pages/app/Staff";
import VisitNotes from "./pages/app/VisitNotes";
import LogBook from "./pages/app/LogBook";
import PlatformPayouts from "./pages/app/PlatformPayouts";
import SuperAdminDashboard from "./pages/app/SuperAdminDashboard";
import SuperAdminAnalytics from "./pages/app/SuperAdminAnalytics";
import SuperAdminPayments from "./pages/app/SuperAdminPayments";
import SuperAdminBusinessDetail from "./pages/app/SuperAdminBusinessDetail";
import NotFound from "./pages/NotFound";
import PayReturn from "./pages/pay/PayReturn";
import PayRemainingPage from "./pages/pay/PayRemainingPage";
import StaffPortal from "./pages/portal/StaffPortal";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import Sales from "./pages/app/Sales";
import Onboarding, { ONBOARDING_SEEN_KEY } from "./pages/Onboarding";
const Discover = lazy(() => import("./pages/Discover"));
const Collections = lazy(() => import("./pages/Collections"));
const CollectionDetail = lazy(() => import("./pages/CollectionDetail"));
const CollectionLookViewer = lazy(() => import("./pages/CollectionLookViewer"));

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

const queryClient = new QueryClient();
const NATIVE_PUSH_PROMPTED_KEY_PREFIX = "buukd:native-push-permission-prompted";

function getNativeEntryPath(): string {
  try {
    return localStorage.getItem(ONBOARDING_SEEN_KEY) === "1" ? "/signin" : "/onboarding";
  } catch {
    return "/onboarding";
  }
}

/** App route path prefixes -> tab title. Longest match wins; only runs for /app/* so public pages keep their usePageMeta titles. */
const APP_TITLES: { prefix: string; title: string }[] = [
  { prefix: "/app/super-admin/business/", title: "Business – Buukd" },
  { prefix: "/app/super-admin/analytics", title: "Super Admin Analytics – Buukd" },
  { prefix: "/app/super-admin/payments", title: "Super Admin Payments – Buukd" },
  { prefix: "/app/super-admin", title: "Super Admin – Buukd" },
  { prefix: "/app/platform/payouts", title: "Settlements – Buukd" },
  { prefix: "/app/access-desk/ticket/", title: "Ticket – Buukd" },
  { prefix: "/app/access-desk", title: "Access desk – Buukd" },
  { prefix: "/app/events/preview/", title: "Event preview – Buukd" },
  { prefix: "/app/events", title: "Events – Buukd" },
  { prefix: "/app/invoicing/preview/", title: "Invoice preview – Buukd" },
  { prefix: "/app/invoicing", title: "Invoicing – Buukd" },
  { prefix: "/app/menu-items/manage", title: "Manage menu items – Buukd" },
  { prefix: "/app/menu-items", title: "Menu items – Buukd" },
  { prefix: "/app/visit-notes", title: "Visit notes – Buukd" },
  { prefix: "/app/booking-page", title: "Booking page – Buukd" },
  { prefix: "/app/orders-page", title: "Orders page – Buukd" },
  { prefix: "/app/catering-requests", title: "Catering requests – Buukd" },
  { prefix: "/app/orders", title: "Orders – Buukd" },
  { prefix: "/app/analytics", title: "Reports – Buukd" },
  { prefix: "/app/appointments", title: "Appointments – Buukd" },
  { prefix: "/app/log-book", title: "Log book – Buukd" },
  { prefix: "/app/sales", title: "Sales – Buukd" },
  { prefix: "/app/services", title: "Services – Buukd" },
  { prefix: "/app/staff", title: "Staff – Buukd" },
  { prefix: "/app/customers", title: "Customers – Buukd" },
  { prefix: "/app/marketing", title: "Marketing – Buukd" },
  { prefix: "/app/billing", title: "Billing – Buukd" },
  { prefix: "/app/usage", title: "Usage – Buukd" },
  { prefix: "/app/memberships", title: "Memberships – Buukd" },
  { prefix: "/app/settings", title: "Settings – Buukd" },
  { prefix: "/app", title: "Dashboard – Buukd" },
];

function DocumentTitle() {
  const { pathname } = useLocation();
  useEffect(() => {
    if (!pathname.startsWith("/app")) return;
    const entry = APP_TITLES.find(
      ({ prefix }) =>
        pathname === prefix ||
        pathname.startsWith(prefix + "/") ||
        (prefix.endsWith("/") && pathname.startsWith(prefix))
    );
    document.title = entry?.title ?? "Buukd";
  }, [pathname]);
  return null;
}

function GaPageViewTracker() {
  const location = useLocation();

  useEffect(() => {
    if (!import.meta.env.PROD) return;
    if (typeof window === "undefined" || typeof window.gtag !== "function") return;

    const path = location.pathname + location.search;
    // Defer to the next task so DocumentTitle and page-level usePageMeta effects
    // have already updated document.title before we read it.
    const timer = setTimeout(() => {
      window.gtag!("event", "page_view", {
        page_location: getBaseUrl() + path,
        page_path: path,
        page_title: document.title,
      });
    }, 0);

    return () => clearTimeout(timer);
  }, [location]);

  return null;
}

function NativeDeepLinkHandler() {
  const navigate = useNavigate();

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    let disposed = false;

    const handleIncomingUrl = (url?: string | null) => {
      if (!url) return;

      try {
        const parsed = new URL(url);
        const routePath =
          parsed.protocol === "http:" && parsed.host === "app"
            ? parsed.pathname || "/"
            : `/${parsed.host}${parsed.pathname}`.replace(/\/{2,}/g, "/");
        const route = `${routePath}${parsed.search || ""}${parsed.hash || ""}`;

        if (
          routePath.startsWith("/app") ||
          routePath.startsWith("/portal") ||
          routePath.startsWith("/pay/return")
        ) {
          navigate(route, { replace: true });
        }
      } catch (error) {
        console.warn("Failed to parse incoming app URL:", error);
      }
    };

    void CapacitorApp.getLaunchUrl()
      .then(({ url }) => {
        if (!disposed) handleIncomingUrl(url);
      })
      .catch((error) => {
        console.warn("Failed to read launch URL:", error);
      });

    const listener = CapacitorApp.addListener("appUrlOpen", ({ url }) => {
      handleIncomingUrl(url);
    });

    return () => {
      disposed = true;
      listener.then((handle) => handle.remove());
    };
  }, [navigate]);

  return null;
}

function NativePushRegistrationSync() {
  const { user } = useAuth();
  const { business } = useBusiness();

  useEffect(() => {
    if (!isNativePushPlatform()) return;

    if (!user?.id) {
      void clearNativePushExternalUser().catch((error) => {
        console.warn("Failed to clear native OneSignal user:", error);
      });
      return;
    }

    if (!business?.id) return;

    const promptedKey = `${NATIVE_PUSH_PROMPTED_KEY_PREFIX}:${user.id}`;
    const hasPrompted = window.localStorage.getItem(promptedKey) === "true";

    void syncNativePushSubscription({
      businessId: business.id,
      userId: user.id,
      userAgent: navigator.userAgent,
      requestPermission: !hasPrompted,
    })
      .then(() => {
        if (!hasPrompted) {
          window.localStorage.setItem(promptedKey, "true");
        }
      })
      .catch((error) => {
        if (!hasPrompted) {
          window.localStorage.setItem(promptedKey, "true");
        }
        console.warn("Native push registration sync skipped:", error);
      });
  }, [business?.id, user?.id]);

  return null;
}

function NativeStatusBarRouteThemeSync() {
  const { pathname } = useLocation();

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    let cancelled = false;

    void (async () => {
      try {
        const { StatusBar, Style } = await import("@capacitor/status-bar");
        if (cancelled) return;

        const isOnboarding = pathname === "/onboarding" || pathname.startsWith("/onboarding/");
        const platform = Capacitor.getPlatform();

        if (isOnboarding) {
          // Onboarding: Full screen immersive mode with overlay
          if (platform === "android") {
            await StatusBar.setOverlaysWebView({ overlay: true });
            if (cancelled) return;
            await StatusBar.setBackgroundColor({ color: "#6f5bd1" }); // Match gradient
            if (cancelled) return;
          }
          await StatusBar.setStyle({ style: Style.Light }); // White text for dark gradient
        } else {
          // App pages: Normal status bar with dedicated space
          if (platform === "android") {
            await StatusBar.setOverlaysWebView({ overlay: false });
            if (cancelled) return;
            await StatusBar.setBackgroundColor({ color: "#ffffff" });
            if (cancelled) return;
          }
          await StatusBar.setStyle({ style: Style.Dark }); // Dark text for light background
        }
      } catch {
        /* plugin unavailable */
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [pathname]);

  return null;
}

const AppGate = ({ children }: { children: React.ReactNode }) => (
  <LocationProvider>
    <SubscriptionGate>
      <TrialGate>{children}</TrialGate>
    </SubscriptionGate>
  </LocationProvider>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <BusinessProvider>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
            <DocumentTitle />
            <GaPageViewTracker />
            <NativeDeepLinkHandler />
            <NativeStatusBarRouteThemeSync />
            <NativePushRegistrationSync />
            <Routes>
              {/* Public routes */}
              <Route
                path="/"
                element={isNativeApp() ? <Navigate to={getNativeEntryPath()} replace /> : <Index />}
              />
              <Route
                path="/index.html"
                element={<Navigate to={isNativeApp() ? getNativeEntryPath() : "/"} replace />}
              />
              <Route path="/onboarding" element={<Onboarding />} />
              <Route
                path="/discover"
                element={
                  <Suspense
                    fallback={
                      <div className="flex h-[100dvh] items-center justify-center bg-black text-white">
                        Loading…
                      </div>
                    }
                  >
                    <Discover />
                  </Suspense>
                }
              />
              <Route
                path="/collections"
                element={
                  <Suspense
                    fallback={
                      <div className="flex h-[100dvh] items-center justify-center bg-black text-white">
                        Loading…
                      </div>
                    }
                  >
                    <Collections />
                  </Suspense>
                }
              />
              <Route
                path="/collections/:collectionId"
                element={
                  <Suspense
                    fallback={
                      <div className="flex h-[100dvh] items-center justify-center bg-black text-white">
                        Loading…
                      </div>
                    }
                  >
                    <CollectionDetail />
                  </Suspense>
                }
              />
              <Route
                path="/collections/view/:lookId"
                element={
                  <Suspense
                    fallback={
                      <div className="flex h-[100dvh] items-center justify-center bg-black text-white">
                        Loading…
                      </div>
                    }
                  >
                    <CollectionLookViewer />
                  </Suspense>
                }
              />
              <Route path="/solutions" element={<Solutions />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/faq" element={<FAQ />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/delete-account" element={<DeleteAccount />} />
              <Route path="/signin" element={<SignIn />} />
              <Route path="/signup" element={<SignUp />} />
              <Route path="/signup/verify" element={<SignUpVerify />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/review/:token" element={<ServiceReviewPage />} />
              <Route path="/feedback/:token" element={<ServiceFeedbackPage />} />
              <Route path="/book/:businessSlug" element={<BookingPage />} />
              <Route path="/book/:businessSlug/manage" element={<ManageBooking />} />
              <Route path="/events/:businessSlug/:eventSlug" element={<EventPage />} />
              <Route path="/events/:businessSlug/:eventSlug/ticket/:registrationId" element={<EventTicket />} />
              <Route path="/invoice/:businessSlug/:code" element={<InvoicePage />} />
              <Route path="/membership/:businessSlug/:code/renew" element={<MembershipRenewalPage />} />
              <Route path="/orders/:businessSlug/:linkSlug" element={<OrdersPublicPage />} />
              <Route path="/order/:businessSlug/:menuSlug" element={<FoodOrderPage />} />
              <Route path="/order/:businessSlug" element={<FoodOrderPage />} />
              <Route path="/catering/:businessSlug/request" element={<CateringRequestPage />} />
              <Route path="/pay/return" element={<PayReturn />} />
              <Route path="/pay/:businessSlug" element={<PayRemainingPage />} />
              <Route path="/portal/:portalId" element={<StaffPortal />} />
              
              {/* Short booking URL - must be after specific routes but before app routes */}
              <Route path="/:businessSlug" element={<BookingPage />} />
              
              {/* App routes - Protected by SubscriptionGate and LocationProvider */}
              <Route path="/app" element={<AppGate><Dashboard /></AppGate>} />
              <Route path="/app/appointments" element={<AppGate><Appointments /></AppGate>} />
              <Route path="/app/visit-notes" element={<AppGate><VisitNotes /></AppGate>} />
              <Route path="/app/visit-notes/:appointmentId" element={<AppGate><VisitNotes /></AppGate>} />
              <Route path="/app/log-book" element={<AppGate><LogBook /></AppGate>} />
              <Route path="/app/services" element={<AppGate><Services /></AppGate>} />
              <Route path="/app/staff" element={<AppGate><Staff /></AppGate>} />
              <Route path="/app/customers" element={<AppGate><Customers /></AppGate>} />
              <Route path="/app/marketing" element={<AppGate><Marketing /></AppGate>} />
              <Route path="/app/analytics" element={<AppGate><Analytics /></AppGate>} />
              <Route path="/app/usage" element={<AppGate><Billing /></AppGate>} />
              <Route path="/app/billing" element={<AppGate><Billing /></AppGate>} />
              <Route path="/app/booking-page" element={<AppGate><BookingPagePreview /></AppGate>} />
              <Route path="/app/memberships" element={<AppGate><Memberships /></AppGate>} />
              <Route path="/app/access-desk" element={<AppGate><AccessDesk /></AppGate>} />
              <Route path="/app/access-desk/ticket/:id" element={<AppGate><AccessDeskTicket /></AppGate>} />
              <Route path="/app/events" element={<AppGate><Events /></AppGate>} />
              <Route path="/app/events/preview/:id" element={<AppGate><EventPreview /></AppGate>} />
              <Route path="/app/invoicing" element={<AppGate><Invoicing /></AppGate>} />
              <Route path="/app/invoicing/preview/:id" element={<AppGate><InvoicePreview /></AppGate>} />
              {/* Catering module: new non-weekly orders flow */}
              <Route path="/app/orders" element={<AppGate><FoodOrders /></AppGate>} />
              <Route path="/app/catering-requests" element={<AppGate><CateringRequestsPage /></AppGate>} />
              <Route path="/app/menu-items" element={<AppGate><MenuItems /></AppGate>} />
              <Route path="/app/menu-items/manage" element={<AppGate><MenuItemsManager /></AppGate>} />
              <Route path="/app/orders-page" element={<AppGate><OrdersPagePreview /></AppGate>} />
              <Route path="/app/sales" element={<AppGate><Sales /></AppGate>} />
              <Route path="/app/settings" element={<AppGate><Settings /></AppGate>} />
              <Route path="/app/platform/payouts" element={<AppGate><PlatformPayouts /></AppGate>} />
              
              {/* Super Admin routes */}
              <Route path="/app/super-admin" element={<AppGate><SuperAdminDashboard /></AppGate>} />
              <Route path="/app/super-admin/analytics" element={<AppGate><SuperAdminAnalytics /></AppGate>} />
              <Route path="/app/super-admin/payments" element={<AppGate><SuperAdminPayments /></AppGate>} />
              <Route path="/app/super-admin/business/:id" element={<AppGate><SuperAdminBusinessDetail /></AppGate>} />
              
              {/* Catch-all */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </BrowserRouter>
        </TooltipProvider>
      </BusinessProvider>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;
